import { extend } from 'flarum/common/extend';
import PostUser from 'flarum/forum/components/PostUser';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import openUserStories from './openUserStories';
import store from './storyStore';

/**
 * Wrap a view hook so a failure inside it can't take the page down.
 *
 * These run on components other extensions also modify. A ring is cosmetic;
 * leaving it off is always better than breaking the page it sits on.
 */
function safely(fn) {
  return function (vnode) {
    try {
      fn.call(this, vnode);
    } catch (e) {
      // Intentionally swallowed — see above.
    }
  };
}

/**
 * Give avatars of users with an active story a ring that opens their stories.
 *
 * Applies to the discussion list only.
 *
 * Posts are excluded because their avatars already carry a group-coloured
 * border and an attached group badge — a third ring there is unreadable, so
 * inside a post the story shows as a header badge instead (see StoryBadge).
 *
 * Profile pages are excluded because a theme extension rewrites that page and
 * draws the avatar from its own component; hooking it there raised inside
 * another extension's render and left the profile blank.
 *
 * The avatar vnode is decorated in place — a class plus a click handler — and
 * never wrapped: an extra element shifts the row's layout and intercepts the
 * click meant for the avatar.
 */
export default function applyAvatarRings() {
  // Kick the lookup off from oninit, never from view: Mithril swallows redraws
  // requested mid-render, so a response arriving then would leave the rings
  // unpainted until some unrelated redraw happened.
  [PostUser, DiscussionListItem].forEach((component) => {
    extend(component.prototype, 'oninit', function () {
      store.load();
    });
  });

  // Author avatar in the discussion list.
  extend(DiscussionListItem.prototype, 'view', safely(function (vnode) {
    const user = this.attrs.discussion && this.attrs.discussion.user();

    if (!user) return;

    ring(findDeep(vnode, isAvatar), user);
  }));
}

/**
 * Turn an avatar vnode into a story ring.
 *
 * Does nothing when the node is missing or the user has no story, so posts by
 * everyone else keep core's exact markup and behaviour.
 */
function ring(avatar, user) {
  if (!avatar || !user || typeof user.id !== 'function') return;
  if (!store.hasStory(user.id())) return;

  avatar.attrs = avatar.attrs || {};

  const seen = store.isSeen(user.id());
  const existing = avatar.attrs.className || '';

  avatar.attrs.className = [
    existing,
    'StoryRing',
    seen ? 'StoryRing--seen' : '',
    // Followers-only stories ring green, so the audience reads at a glance.
    store.isFollowerOnly(user.id()) ? 'StoryRing--followers' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Handled on the avatar itself so it beats the link wrapping it. Whatever
  // handler was already there still runs — overwriting it broke components
  // that rely on their own click behaviour.
  const previous = avatar.attrs.onclick;

  avatar.attrs.onclick = function (e) {
    e.preventDefault();
    e.stopPropagation();

    openUserStories(user);

    if (typeof previous === 'function') {
      return previous.apply(this, arguments);
    }
  };
}

// ── vdom helpers ───────────────────────────────────────────────────────────

function isAvatar(node) {
  const className = node && node.attrs && node.attrs.className;

  return typeof className === 'string' && /\bAvatar\b/.test(className);
}

/**
 * Search a whole subtree for the first match.
 *
 * Used only for the discussion list, whose avatar sits behind markup that
 * varies more between Flarum versions and themes than the post header does.
 */
function findDeep(vnode, predicate) {
  if (!vnode || typeof vnode !== 'object') return null;

  if (Array.isArray(vnode)) {
    for (const child of vnode) {
      const found = findDeep(child, predicate);
      if (found) return found;
    }
    return null;
  }

  if (predicate(vnode)) return vnode;

  return findDeep(vnode.children, predicate);
}
